import { useMemo, useState, type ReactNode } from "react";

export interface DataTableColumn<T> {
  key: string;
  header: ReactNode;
  /** Render cella. Riceve la riga e l'indice. */
  cell: (row: T, idx: number) => ReactNode;
  /** Funzione di sort opzionale. Se omessa la colonna non è ordinabile. */
  sortValue?: (row: T) => string | number | Date | null | undefined;
  className?: string;
  align?: "left" | "right" | "center";
}

interface DataTableProps<T> {
  columns: DataTableColumn<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  /** Numero massimo di righe per pagina. Default 25, 0 = no pagination. */
  pageSize?: number;
  /** Empty state custom. */
  empty?: ReactNode;
  /** Se presente le righe diventano cliccabili (es. apertura di un drawer di
   *  dettaglio). I bottoni dentro le celle devono fare `stopPropagation()`. */
  onRowClick?: (row: T) => void;
}

/**
 * Tabella generica con sort + pagination client-side.
 *
 * Volutamente minimalista: per dataset grandi (>1000 righe) o filtri
 * server-side meglio usare un componente dedicato. Qui copriamo i casi
 * tipici del backoffice (markets, withdrawals, audit log).
 */
export function DataTable<T>({
  columns,
  rows,
  rowKey,
  pageSize = 25,
  empty = (
    <div className="px-4 py-8 text-center text-muted text-[13px]">
      Nessun risultato.
    </div>
  ),
  onRowClick,
}: DataTableProps<T>) {
  const [sort, setSort] = useState<{ key: string; dir: "asc" | "desc" } | null>(
    null,
  );
  const [page, setPage] = useState(0);

  const sortedRows = useMemo(() => {
    if (!sort) return rows;
    const col = columns.find((c) => c.key === sort.key);
    if (!col?.sortValue) return rows;
    const factor = sort.dir === "asc" ? 1 : -1;
    return [...rows].sort((a, b) => {
      const va = col.sortValue!(a);
      const vb = col.sortValue!(b);
      if (va == null && vb == null) return 0;
      if (va == null) return 1; // nulli sempre in coda
      if (vb == null) return -1;
      if (va < vb) return -1 * factor;
      if (va > vb) return 1 * factor;
      return 0;
    });
  }, [rows, sort, columns]);

  const totalPages = pageSize > 0 ? Math.max(1, Math.ceil(sortedRows.length / pageSize)) : 1;
  const safePage = Math.min(page, totalPages - 1);
  const pageRows =
    pageSize > 0
      ? sortedRows.slice(safePage * pageSize, (safePage + 1) * pageSize)
      : sortedRows;

  function toggleSort(key: string) {
    setSort((cur) => {
      if (!cur || cur.key !== key) return { key, dir: "asc" };
      if (cur.dir === "asc") return { key, dir: "desc" };
      return null;
    });
    setPage(0);
  }

  return (
    <div className="card p-0 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-[13px]">
          <thead className="border-b border-line bg-bg-2">
            <tr className="text-left text-muted text-[11px] uppercase tracking-wider">
              {columns.map((col) => {
                const sortable = !!col.sortValue;
                const isSorted = sort?.key === col.key;
                const arrow = isSorted ? (sort.dir === "asc" ? "↑" : "↓") : "";
                const align =
                  col.align === "right"
                    ? "text-right"
                    : col.align === "center"
                      ? "text-center"
                      : "text-left";
                return (
                  <th
                    key={col.key}
                    className={`px-4 py-3 font-medium ${align} ${col.className ?? ""}`}
                  >
                    {sortable ? (
                      <button
                        type="button"
                        onClick={() => toggleSort(col.key)}
                        className="inline-flex items-center gap-1 hover:text-text-2 transition-colors"
                      >
                        {col.header}
                        <span className="text-[10px]">{arrow || "↕"}</span>
                      </button>
                    ) : (
                      col.header
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {pageRows.length === 0 ? (
              <tr>
                <td colSpan={columns.length}>{empty}</td>
              </tr>
            ) : (
              pageRows.map((row, idx) => (
                <tr
                  key={rowKey(row)}
                  className={`border-b border-line last:border-b-0 hover:bg-bg-2 transition-colors ${
                    onRowClick ? "cursor-pointer" : ""
                  }`}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                >
                  {columns.map((col) => {
                    const align =
                      col.align === "right"
                        ? "text-right"
                        : col.align === "center"
                          ? "text-center"
                          : "text-left";
                    return (
                      <td
                        key={col.key}
                        className={`px-4 py-3 ${align} ${col.className ?? ""}`}
                      >
                        {col.cell(row, idx)}
                      </td>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {pageSize > 0 && totalPages > 1 && (
        <div className="flex items-center justify-between px-4 py-2 border-t border-line text-[12px] text-muted">
          <div>
            Pagina {safePage + 1} di {totalPages} ({sortedRows.length} righe)
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              className="btn-secondary text-[11.5px] py-1 px-3"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={safePage === 0}
            >
              Indietro
            </button>
            <button
              type="button"
              className="btn-secondary text-[11.5px] py-1 px-3"
              onClick={() =>
                setPage((p) => Math.min(totalPages - 1, p + 1))
              }
              disabled={safePage >= totalPages - 1}
            >
              Avanti
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
